import { loop, Loop } from 'redux-loop';

import { HttpError } from '../utils/HttpError';
import { runLoopFetch } from '../utils/loop';
import {
  SingleCalculationResultsTTT,
  SteelValuesTTT,
  JobId,
  TttCalculationResult
} from '../../types/Calculation';
import auth0Client from 'src/Auth';
import { API_BASE } from 'src/config';
import { TTT_ISOLINE_LEVELS, TTT_REGION_HINTS } from 'src/utils/TTT';

export const SINGLE_CALC_FETCH_START = 'SINGLE_CALC_TTT_ARBCR_FETCH_START';
export const SINGLE_CALC_FETCH_SUCCESS = 'SINGLE_CALC_TTT_ARBCR_FETCH_SUCCESS';
export const SINGLE_CALC_FETCH_FAILURE = 'SINGLE_CALC_TTT_ARBCR_FETCH_FAILURE';
export const SINGLE_CALC_POLL_SUCCESS =
  'SINGLE_CALC_TTT_POLL_SUCCESS';
export const SINGLE_CALC_POLL_FAILURE =
  'SINGLE_CALC_TTT_POLL_FAILURE';

type CalculationAction =
  | { type: typeof SINGLE_CALC_FETCH_START; steelValues: SteelValuesTTT }
  | {
      type: typeof SINGLE_CALC_FETCH_SUCCESS;
      payload: JobId;
    }
  | { type: typeof SINGLE_CALC_POLL_SUCCESS; payload: SingleCalculationResultsTTT }
  | { type: typeof SINGLE_CALC_FETCH_FAILURE; payload?: any }
  | { type: typeof SINGLE_CALC_POLL_FAILURE; payload?: any };
export interface CalculationState {
  singleCalcResults: SingleCalculationResultsTTT | null;
  fetching: boolean;
  error: { status?: number; message: string } | null;
}

const initialState: CalculationState = {
  fetching: false,
  singleCalcResults: null,
  error: null,
};

// JSON parsing - needed? START
const parseBackendJsonString = (value: string): any => {
  try {
    return JSON.parse(value);
  } catch (_e) {
    return JSON.parse(value.replace(/\bNaN\b|[-+]?Infinity\b/g, 'null'));
  }
};



const isolineKeys = [
  'level_pct',
  'log10_plot_time',
  'region_hint',
  'temperature_C'
];

const validateTttIsolines = (result: TttCalculationResult): void => {
  const presentKeys = isolineKeys.filter(
    key => (result as any)[key] !== undefined
  );

  if (presentKeys.length === 0) {
    return;
  }

  if (presentKeys.length !== isolineKeys.length) {
    throw new Error(
      'Unexpected TTT result format: all isoline arrays are required'
    );
  }

  const arrays = isolineKeys.map(key => (result as any)[key]);
  if (!arrays.every(Array.isArray)) {
    throw new Error(
      'Unexpected TTT result format: isoline fields must be arrays'
    );
  }

  const expectedLength = arrays[0].length;
  if (expectedLength === 0) {
    throw new Error(
      'Unexpected TTT result format: isoline arrays must not be empty'
    );
  }

  if (!arrays.every(values => values.length === expectedLength)) {
    throw new Error(
      'Unexpected TTT result format: isoline arrays have different lengths'
    );
  }

  if (
    !result.level_pct!.every(
      level =>
        Number.isFinite(level) && TTT_ISOLINE_LEVELS.indexOf(level) !== -1
    )
  ) {
    throw new Error(
      'Unexpected TTT result format: level_pct contains unsupported values'
    );
  }

  if (
    !result.region_hint!.every(
      region => TTT_REGION_HINTS.indexOf(region) !== -1
    )
  ) {
    throw new Error(
      'Unexpected TTT result format: region_hint contains unsupported values'
    );
  }

  if (
    !result.log10_plot_time!.every(Number.isFinite) ||
    !result.temperature_C!.every(Number.isFinite)
  ) {
    throw new Error(
      'Unexpected TTT result format: isoline coordinates contain invalid values'
    );
  }
};

export const normalizeTttResult = (
  payload: any
): SingleCalculationResultsTTT => {
  let normalized = payload;

  if (typeof normalized === 'string') {
    normalized = parseBackendJsonString(normalized);
  }

  if (typeof normalized?.Result === 'string') {
    normalized = {
      ...normalized,
      Result: parseBackendJsonString(normalized.Result)
    };
  }

  const hasResultWrapper = normalized?.Result !== undefined;
  let result = normalized?.Result !== undefined
    ? normalized.Result
    : normalized;

  for (let depth = 0; depth < 4; depth += 1) {
    if (typeof result === 'string') {
      result = parseBackendJsonString(result);
      continue;
    }

    if (result?.Result !== undefined) {
      result = result.Result;
      continue;
    }

    break;
  }

  if (!Array.isArray(result?.temp)) {
    throw new Error('Unexpected TTT result format: Result.temp is missing');
  }

  validateTttIsolines(result);

  return hasResultWrapper
    ? {
      ...normalized,
      Result: result
    }
    : { Result: result };
};

// JSON parsing - needed? END


const fetchSingleCalculation = async (
  steelValues: SteelValuesTTT
): Promise<JobId> => {
  const res = await fetch(`${API_BASE}/calculation/singleTTT`, {
    body: JSON.stringify(steelValues),
    credentials: 'same-origin',
    headers: {
      Authorization: `Bearer ${auth0Client.getIdToken()}`,
      'content-type': 'application/json'
    },
    method: 'POST'
  });

  if (!res.ok) {
    console.log(steelValues);
    console.log(res);
    throw new HttpError({ status: res.status, message: res.statusText });
  }

  return res.json();
};

/**
 * Redux-action creator.
 *
 * @param steelValues the alloy components etc.
 */
export const initFetchSingleCalculation = (steelValues: SteelValuesTTT) => ({
  steelValues,
  type: SINGLE_CALC_FETCH_START
});



const wait = (ms:number) => new Promise(res => setTimeout(res, ms));


const fetchSingleResult = async (
  jobid: JobId, count: number
): Promise<SingleCalculationResultsTTT> => {
  
  const jid = String((jobid as any)?.id ?? (jobid as any)?.jobid ?? jobid);
  const res = await fetch(`${API_BASE}/calculation/singleTTTResult/` + encodeURIComponent(jid) + `?wait=true&maxWaitMs=25000`, {
    credentials: 'same-origin',
    headers: {
      Authorization: `Bearer ${auth0Client.getIdToken()}`,
      'content-type': 'application/json'
    },
    method: 'GET'
  });

  if (!res.ok) {
    throw new HttpError({ status: res.status, message: res.statusText });
  }
  if (res.status !== 200) {
    if (count++ < 80) {
      await wait(15000);
      return fetchSingleResult(jobid, count);
    } else {
      throw new Error('max retries reached');
    };
  }
  
  return normalizeTttResult(await res.json());
};

/**
 * Redux-reducer.
 */
export function calculation(
  state: CalculationState = initialState,
  action: CalculationAction
): CalculationState | Loop<CalculationState, any> {
  switch (action.type) {
    case SINGLE_CALC_FETCH_START:
      return loop(
        { ...state, fetching: true, error: null },
        runLoopFetch(
          fetchSingleCalculation,
          SINGLE_CALC_FETCH_SUCCESS,
          SINGLE_CALC_FETCH_FAILURE,
          [action.steelValues]
        )
      );
      case SINGLE_CALC_FETCH_SUCCESS:
        return loop(
          { ...state, fetching: true, error: null },
          runLoopFetch(
            fetchSingleResult,
            SINGLE_CALC_POLL_SUCCESS,
            SINGLE_CALC_POLL_FAILURE,
            [action.payload, 0]
          )
        );
  
      case SINGLE_CALC_POLL_SUCCESS:
        // What is in ...state? isn't it just fetching and singleCalcResults?
        return { ...state, fetching: false, singleCalcResults: action.payload, error: null };
  
      case SINGLE_CALC_POLL_FAILURE:
        return { ...state, fetching: false, singleCalcResults: null, error: { status: (action as any)?.payload?.status, message: (action as any)?.payload?.message || 'Request failed' } };

    case SINGLE_CALC_FETCH_FAILURE:
      return { ...state, fetching: false, singleCalcResults: null, error: { status: (action as any)?.payload?.status, message: (action as any)?.payload?.message || 'Request failed' } };

    default:
      return state;
  }
}
